import { useState } from 'react'
import { Box, Wrap, WrapItem, Button, Text, Stack } from '@chakra-ui/react'
import { FiX } from 'react-icons/fi'
import { SheetSelect } from '../shared/SheetSelect'

export interface DistrictFilterValue {
  stateHouseDistricts: string[]
  stateSenateDistricts: string[]
  congressionalDistricts: string[]
}

export type DistrictFilterOptions = DistrictFilterValue

export const EMPTY_DISTRICT_FILTER: DistrictFilterValue = {
  stateHouseDistricts: [],
  stateSenateDistricts: [],
  congressionalDistricts: [],
}

export type ChamberKey = keyof DistrictFilterValue

const CHAMBERS: { key: ChamberKey; label: string }[] = [
  { key: 'stateHouseDistricts', label: 'House' },
  { key: 'stateSenateDistricts', label: 'Senate' },
  { key: 'congressionalDistricts', label: 'Congress' },
]

interface DistrictFilterProps {
  options: DistrictFilterOptions
  value: DistrictFilterValue
  onChange: (next: DistrictFilterValue) => void
  label?: string
  /**
   * Optional chamber to seed the initial active tab with, when `value` itself carries no
   * selection yet (e.g. a caller remounting this component ahead of an async prefill landing
   * in `value`). Only consulted if that chamber has options; otherwise falls through to the
   * `available[0]` default. Never overrides a chamber that already has a selected value.
   */
  activeChamber?: ChamberKey
}

/**
 * Legislative-district filter for contact search (parked item #2). The organizer picks one chamber
 * (House / Senate / Congressional) then one or more district values within it; matches contacts
 * whose voter record is in any selected district (OR). Options are the actual district values stored
 * on the committee's own contacts, so a searchable select over them needs no normalization.
 *
 * One chamber is active at a time: switching chamber clears the current selection, so the emitted
 * value never has more than one chamber's list populated. Self-hides when no chamber has options.
 * value/onChange plug into both useState and a react-hook-form Controller.
 */
export function DistrictFilter({ options, value, onChange, label = 'District', activeChamber }: DistrictFilterProps) {
  const available = CHAMBERS.filter((c) => options[c.key].length > 0)

  // Active chamber, seeded once (switching is user-driven from there), in priority order:
  // (1) the chamber that already carries a chosen value in `value`; (2) the caller-supplied
  // `activeChamber` hint, if it has options; (3) the first available chamber; (4) the historical
  // hardcoded fallback.
  const [chamber, setChamber] = useState<ChamberKey>(
    () =>
      CHAMBERS.find((c) => value[c.key].length > 0)?.key ??
      (activeChamber && options[activeChamber].length > 0 ? activeChamber : undefined) ??
      available[0]?.key ??
      'stateHouseDistricts',
  )

  if (available.length === 0) return null

  const selected = value[chamber]
  const addOptions = options[chamber]
    .filter((d) => !selected.includes(d))
    .map((d) => ({ value: d, label: d }))

  const switchChamber = (next: ChamberKey) => {
    if (next === chamber) return
    setChamber(next)
    onChange(EMPTY_DISTRICT_FILTER) // switching chamber clears the current selection
  }

  const addDistrict = (district: string) => {
    if (!district || selected.includes(district)) return
    onChange({ ...EMPTY_DISTRICT_FILTER, [chamber]: [...selected, district] })
  }

  const removeDistrict = (district: string) => {
    onChange({ ...EMPTY_DISTRICT_FILTER, [chamber]: selected.filter((d) => d !== district) })
  }

  return (
    <Box>
      {label && <Text fontSize="sm" color="ink.2" mb={1}>{label}</Text>}
      <Stack gap={2} align="stretch">
        <Wrap gap={2}>
          {available.map((c) => {
            const active = c.key === chamber
            return (
              <WrapItem key={c.key}>
                <Button
                  type="button"
                  size="sm"
                  minH="44px"
                  borderRadius="full"
                  variant={active ? 'solid' : 'outline'}
                  colorPalette={active ? 'brand' : 'gray'}
                  aria-pressed={active}
                  onClick={() => switchChamber(c.key)}
                >
                  {c.label}
                </Button>
              </WrapItem>
            )
          })}
        </Wrap>

        <Box maxW="260px">
          <SheetSelect
            aria-label="Add district"
            placeholder="Add district…"
            searchable
            size="sm"
            options={addOptions}
            value=""
            onChange={addDistrict}
          />
        </Box>

        {selected.length > 0 && (
          <Wrap gap={2}>
            {selected.map((d) => (
              <WrapItem key={d}>
                <Button
                  type="button"
                  size="sm"
                  minH="44px"
                  borderRadius="full"
                  variant="solid"
                  colorPalette="brand"
                  aria-label={`Remove district ${d}`}
                  onClick={() => removeDistrict(d)}
                >
                  {d} <FiX />
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        )}
      </Stack>
    </Box>
  )
}
